import { isPilotPointName, legForEndPoint, pruneRouteGeometry } from './helpers';
import { VoyagePlan } from '../models/vessel-planning-model';
import { VoyageRoutePointFeature, VoyageRouteLineFeature, VoyageRoute } from '../models/voyage-routing-model';
import { ImportResult } from './types/converters.types';

const MPS2KNOT = (60*60)/1852;

export class JRCConverter {
    static check(str: string) {
        try {
            // The JRC export is a CSV file with comment lines starting with '//', first WP has ID '000'
            if (!(str.startsWith('//') || str.startsWith('000'))) return;

            // WPT No.,LAT,,,LON,,,PORT[NM],STBD[NM],Speed[kn],Sail(RL/GC),ROT[deg/min],Turn Rad[NM],Time Zone,,Name
            const lines: string[][] = str.split(/\r?\n/)
            .map(line => line.split(','))
            .filter(line => line.length >= 8 && line[0].length === 3 && Number.isFinite(+line[0]));

            lines.forEach(line => {
                for(let i=0;i<line.length;i++) {
                    line[i] = line[i].trim();
                }
            });

            if (!lines.length) return;

            return lines.map(line => {
                const seq: number = Number.parseInt(line[0]);
                const lat: number = (Number.parseInt(line[1]) + Number.parseFloat(line[2])/60) * (line[3] === 'S' ? -1 : 1);
                const lon: number = (Number.parseInt(line[4]) + Number.parseFloat(line[5])/60) * (line[6] === 'W' ? -1 : 1);
                const portNM: number|undefined = line[7] === '***' ? undefined : Number.parseFloat(line[7]);
                const stbdNM: number|undefined = line[8] === '***' ? undefined : Number.parseFloat(line[8]);
                const speedNM: number|undefined = line[9] === '***' ? undefined : Number.parseFloat(line[9]);
                const trackType: string|undefined = line[10] === '***' ? undefined : line[10];
                const turnSpeed: number|undefined = line[11] === '***' ? undefined : Number.parseFloat(line[11]);
                const turnRadiusNM: number|undefined = line[12] === '***' ? undefined : Number.parseFloat(line[12]);
                const name: string = line[15];

                return {
                    seq,
                    lat,
                    lon,
                    portNM,
                    stbdNM,
                    speedNM,
                    trackType,
                    turnSpeed,
                    turnRadiusNM,
                    name
                }
            });
        } catch(e) {
            // don't care
        }
    }

    static import(str: string, options = {}): ImportResult|undefined {

        try {
            const route_geometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature> = [];

            const waypoints = JRCConverter.check(str);
            if (!waypoints) return;

            let prevCoords: [number, number]|null = null;
            waypoints.forEach(waypoint => {
                const coords: [number, number] = [waypoint.lon, waypoint.lat];

                const name = waypoint.name;
                const isPilotPoint = isPilotPointName(name);

                const wPoint: VoyageRoutePointFeature = {
                    type: 'Feature',
                    geometry: {
                        coordinates: coords,
                        type: 'Point'
                    },
                    properties: {
                        type: 'Hard',
                        name: name,
                        pilotPoint: isPilotPoint ? true : undefined
                    }
                };

                if (prevCoords) {
                    route_geometry.push({
                            type: 'Feature',
                            geometry: {
                                coordinates: [prevCoords, coords],
                                type: 'LineString'
                            },
                            properties: {
                                leg_type: waypoint.trackType === "GC" ? 'GreatCircle' : 'Rhumbline'
                            }
                    });
                }

                route_geometry.push(wPoint);

                prevCoords = coords;
            });

            return {
                route: {
                    routeId: '',
                    route_geometry
                },
                extras: {
                    // Plan related data.
                }
            };
        } catch (error) {
            console.warn(error);
        }
    }

    static export(plan: VoyagePlan, options = {}): string
    {
        const opt = {
            routeName: 'Maersk Voyage Planning Route',
            ...options
        }

        // Bail out if no valid route is selected.
        if (!plan.currentSelectedRouteId)
        {
            throw new Error("No valid route selected in plan!");
        }

        // Only include the selected route.
        const routeIndex = plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId) ?? plan.selectedRouteIndex

        const route = plan.routes[routeIndex];

        const waypoints: string[] = [];

        const prunedRoute = pruneRouteGeometry(route.route_geometry);

        let points = prunedRoute.filter(item => item.geometry.type === "Point") as VoyageRoutePointFeature[];

        const legs = <VoyageRouteLineFeature[]>prunedRoute.filter(item => item.geometry.type === "LineString");

        // Lat: 'DD,MM.SSS,[S|N]', Lon: 'DDD,MM.SSS,[E|W]'
        const coordToArr = (coord: [number, number]) => {
            return [
                Math.abs(Math.trunc(coord[1])).toString().padStart(2, '0'),
                (Math.abs(coord[1] % 1)*60).toFixed(3).padStart(6, '0'),
                coord[1] < 0 ? 'S' : 'N',
                Math.abs(Math.trunc(coord[0])).toString().padStart(3, '0'),
                (Math.abs(coord[0] % 1)*60).toFixed(3).padStart(6, '0'),
                coord[0] < 0 ? 'W' : 'E'
            ]
        };

        let seqNo = 0;
        points.forEach(point => {
            const wp: string[] = [];
            
            wp.push(seqNo.toString().padStart(3, '0'));
            seqNo++;

            wp.push(...coordToArr(point.geometry.coordinates));
            
            // TODO: read following from waypoint if calculated
            // Note: ECDIS apparently fails to import if any of these are set to 0
            let rot = '30.00';
            let turnRadius = '0.20';

            const leg = legForEndPoint(legs, point.geometry.coordinates, 1);
            if (leg && leg.properties) {
                // Read speed from calculation if available; otherwise default to 10 knots
                const speed = ((leg.properties.speed_over_ground || 10/MPS2KNOT)*MPS2KNOT).toFixed(1).padStart(5, '0');
                // TBD: PORT[NM],STBD[NM],Speed[kn], ROT[deg/min],Turn Rad[NM]
                wp.push(...['0.05', '0.05', speed, leg.properties.leg_type === 'GreatCircle' ? 'GC' : 'RL', rot, turnRadius]);
            } else {
                // PORT[NM],STBD[NM],Speed[kn],Sail(RL/GC),ROT[deg/min],Turn Rad[NM]
                wp.push(...['***', '***', '***', '***', '***', '***']);
            }

            // Time Zone (seems to be an offset like 01:00 followed by an 'E' for the files we have)
            wp.push('01:00');
            wp.push('E');
            
            wp.push(point.properties.name || ' ');
            
            waypoints.push(wp.join(','));
        });

        const csvHeader = "// ROUTE SHEET exported by JRC ECDIS.\r\n" +
        "// <<NOTE>>This strings // indicate comment column/cells. You can edit freely.\r\n" +
        `// ${plan.departureTerminalCode || 'DEP_NA'}-${plan.arrivalTerminalCode || 'ARR_NA'},<ANTS>,${opt.routeName}\r\n` +
        "// WPT No.,LAT,,,LON,,,PORT[NM],STBD[NM],Speed[kn],Sail(RL/GC),ROT[deg/min],Turn Rad[NM],Time Zone,,Name\r\n";

        const result = csvHeader + waypoints.join('\r\n') + '\r\n';
        
        return result;
    }
}
